import { MIDI_MIN, MIDI_MAX } from "./midiConstants";

// Gleitzman's pre-rendered GM soundfonts (MusyngKite — higher quality)
const SOUNDFONT_BASE = "https://gleitz.github.io/midi-js-soundfonts/MusyngKite";

// Note names using flats (matches Gleitzman's file naming)
const NOTE_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

function midiToSoundfontKey(midi) {
  const octave = Math.floor(midi / 12) - 1;
  return `${NOTE_NAMES[midi % 12]}${octave}`;
}

// Cache: { [instrumentName]: { [midi]: AudioBuffer } }
const cache = {};

// Loading state: { [instrumentName]: Promise }
const loading = {};

/**
 * Load all note samples for a GM instrument. Returns { [midi]: AudioBuffer }.
 * Results are cached — calling again with the same instrument returns instantly.
 * Notes are available as soon as each individual sample decodes (progressive).
 */
export async function loadInstrument(audioContext, instrumentName) {
  if (cache[instrumentName]) return cache[instrumentName];
  if (loading[instrumentName]) return loading[instrumentName];

  // Create cache entry immediately so notes become available as they load
  const buffers = {};
  cache[instrumentName] = buffers;

  const promise = (async () => {
    const promises = [];
    let loaded = 0;

    for (let midi = MIDI_MIN; midi <= MIDI_MAX; midi++) {
      const key = midiToSoundfontKey(midi);
      const url = `${SOUNDFONT_BASE}/${instrumentName}-mp3/${key}.mp3`;

      promises.push(
        fetch(url)
          .then((r) => {
            if (!r.ok) throw new Error(`${r.status}`);
            return r.arrayBuffer();
          })
          .then((ab) => audioContext.decodeAudioData(ab))
          .then((buffer) => {
            buffers[midi] = buffer;
            loaded++;
          })
          .catch(() => { /* skip missing notes */ })
      );
    }

    await Promise.all(promises);
    delete loading[instrumentName];
    console.log(`[DAW] Loaded ${loaded} samples for ${instrumentName}`);
    return buffers;
  })();

  loading[instrumentName] = promise;
  return promise;
}

/**
 * Get cached instrument buffers (empty object if loading, null if never requested).
 */
export function getCachedInstrument(instrumentName) {
  return cache[instrumentName] || null;
}

/**
 * Check if an instrument is currently loading.
 */
export function isInstrumentLoading(instrumentName) {
  return !!loading[instrumentName];
}

/**
 * Curated list of good-sounding GM instruments.
 */
export const INSTRUMENT_LIST = [
  { id: "acoustic_grand_piano", name: "Grand Piano", category: "Keys" },
  { id: "bright_acoustic_piano", name: "Bright Piano", category: "Keys" },
  { id: "electric_piano_1", name: "Electric Piano", category: "Keys" },
  { id: "electric_piano_2", name: "EP 2 (DX)", category: "Keys" },
  { id: "drawbar_organ", name: "Organ", category: "Keys" },
  { id: "rock_organ", name: "Rock Organ", category: "Keys" },
  { id: "church_organ", name: "Church Organ", category: "Keys" },
  { id: "acoustic_guitar_nylon", name: "Nylon Guitar", category: "Guitar" },
  { id: "acoustic_guitar_steel", name: "Steel Guitar", category: "Guitar" },
  { id: "electric_guitar_clean", name: "Clean Electric", category: "Guitar" },
  { id: "electric_guitar_jazz", name: "Jazz Guitar", category: "Guitar" },
  { id: "overdriven_guitar", name: "Overdriven", category: "Guitar" },
  { id: "distortion_guitar", name: "Distortion", category: "Guitar" },
  { id: "electric_bass_finger", name: "Finger Bass", category: "Bass" },
  { id: "electric_bass_pick", name: "Pick Bass", category: "Bass" },
  { id: "slap_bass_1", name: "Slap Bass", category: "Bass" },
  { id: "synth_bass_1", name: "Synth Bass", category: "Bass" },
  { id: "violin", name: "Violin", category: "Strings" },
  { id: "viola", name: "Viola", category: "Strings" },
  { id: "cello", name: "Cello", category: "Strings" },
  { id: "contrabass", name: "Contrabass", category: "Strings" },
  { id: "string_ensemble_1", name: "Strings Ensemble", category: "Strings" },
  { id: "synth_strings_1", name: "Synth Strings", category: "Strings" },
  { id: "trumpet", name: "Trumpet", category: "Brass" },
  { id: "trombone", name: "Trombone", category: "Brass" },
  { id: "french_horn", name: "French Horn", category: "Brass" },
  { id: "brass_section", name: "Brass Section", category: "Brass" },
  { id: "alto_sax", name: "Alto Sax", category: "Woodwind" },
  { id: "tenor_sax", name: "Tenor Sax", category: "Woodwind" },
  { id: "flute", name: "Flute", category: "Woodwind" },
  { id: "clarinet", name: "Clarinet", category: "Woodwind" },
  { id: "oboe", name: "Oboe", category: "Woodwind" },
  { id: "choir_aahs", name: "Choir", category: "Vocal" },
  { id: "voice_oohs", name: "Voice Oohs", category: "Vocal" },
  { id: "lead_1_square", name: "Square Lead", category: "Synth" },
  { id: "lead_2_sawtooth", name: "Saw Lead", category: "Synth" },
  { id: "pad_1_new_age", name: "New Age Pad", category: "Synth" },
  { id: "pad_2_warm", name: "Warm Pad", category: "Synth" },
  { id: "marimba", name: "Marimba", category: "Perc" },
  { id: "vibraphone", name: "Vibraphone", category: "Perc" },
  { id: "xylophone", name: "Xylophone", category: "Perc" },
  { id: "steel_drums", name: "Steel Drums", category: "Perc" },
  { id: "kalimba", name: "Kalimba", category: "Perc" },
];
